import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { apiClient } from '@dukene/shared';
import { Lock, ArrowRight, AlertCircle, CheckCircle2, XCircle, Eye, EyeOff } from 'lucide-react';

const resetPasswordSchema = z.object({
  newPassword: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const onSubmit = async (data: ResetPasswordFormData) => {
    try {
      setError('');
      setSuccess('');
      setIsLoading(true);
      const response = await apiClient.post<{ detail: string }>('/auth/reset-password', {
        token,
        new_password: data.newPassword,
      });
      setSuccess(response.detail || 'Password reset successfully. You can now sign in.');
    } catch (err: any) {
      setError(err.message || 'Unable to reset the password. The link may be expired or invalid.');
    } finally {
      setIsLoading(false);
    }
  };

  const isMissingToken = !token;

  return (
    <div className="min-h-screen flex bg-white">
      <div className="hidden lg:flex lg:w-1/2 bg-white border-r border-gray-100 relative">
        <div className="flex flex-col justify-between w-full p-16 xl:p-24">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white border border-gray-200 rounded-lg flex items-center justify-center">
              <img src="/favicon.png" alt="Logo" className="w-full h-full object-contain p-0.5" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-black tracking-tight">Dukene</h1>
              <p className="text-xs text-gray-500 font-medium tracking-wide">MERCHANTS</p>
            </div>
          </div>

          <div className="space-y-16">
            <div>
              <h2 className="text-6xl font-light text-black mb-6 leading-tight tracking-tight">
                Set a New
                <br />
                Password
              </h2>
              <div className="w-16 h-0.5 bg-black mb-6" />
              <p className="text-lg text-gray-600 leading-relaxed font-light">
                Choose a strong new password to secure your merchant account and restore access.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-8 text-xs text-gray-400 font-light">
            <span>© 2024 Dukene</span>
            <span className="w-1 h-1 rounded-full bg-gray-300" />
            <span>All rights reserved</span>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center bg-white px-8 lg:px-20 xl:px-32 py-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center justify-center gap-3 mb-16">
            <div className="w-10 h-10 bg-white border border-gray-200 rounded-lg flex items-center justify-center">
              <img src="/favicon.png" alt="Logo" className="w-full h-full object-contain p-0.5" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-black">Dukene</h1>
              <p className="text-xs text-gray-500 font-medium">MERCHANTS</p>
            </div>
          </div>

          <div className="mb-12">
            <h2 className="text-4xl font-light text-black mb-3 tracking-tight">Reset password</h2>
            <p className="text-gray-500 text-base font-light">
              Enter your new password below.
            </p>
          </div>

          {isMissingToken ? (
            <div className="border border-red-200 rounded-xl p-6 bg-red-50">
              <div className="flex items-start gap-4">
                <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="space-y-3">
                  <div>
                    <h3 className="text-lg font-medium text-red-950 mb-1">Missing reset token</h3>
                    <p className="text-sm text-red-800 font-light leading-relaxed">
                      The password reset link is incomplete. Request a new reset email from the login page.
                    </p>
                  </div>
                  <Link
                    to="/forgot-password"
                    className="inline-flex items-center gap-2 text-sm font-medium text-red-900 hover:text-black transition-colors"
                  >
                    Request a new link
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          ) : success ? (
            <div className="border border-emerald-200 rounded-xl p-6 bg-emerald-50">
              <div className="flex items-start gap-4">
                <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="space-y-3">
                  <div>
                    <h3 className="text-lg font-medium text-emerald-950 mb-1">Password updated</h3>
                    <p className="text-sm text-emerald-800 font-light leading-relaxed">{success}</p>
                  </div>
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-2 text-sm font-medium text-emerald-900 hover:text-black transition-colors"
                  >
                    Go to login
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-8 border border-red-200 rounded-lg p-4 bg-red-50">
                  <div className="flex gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-red-900 text-sm mb-1">Error</p>
                      <p className="text-sm text-red-700 font-light">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-black mb-3">
                    New password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      {...register('newPassword')}
                      id="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      placeholder="••••••••"
                      className="h-12 w-full rounded-lg border border-gray-200 bg-white text-black placeholder:text-gray-400 focus:border-black focus:outline-none focus:ring-1 focus:ring-black pl-12 pr-12 text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed font-light"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      tabIndex={-1}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.newPassword && (
                    <p className="text-sm text-red-600 mt-2 font-light">{errors.newPassword.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-black mb-3">
                    Confirm password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      {...register('confirmPassword')}
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      placeholder="••••••••"
                      className="h-12 w-full rounded-lg border border-gray-200 bg-white text-black placeholder:text-gray-400 focus:border-black focus:outline-none focus:ring-1 focus:ring-black pl-12 pr-12 text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed font-light"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      tabIndex={-1}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-sm text-red-600 mt-2 font-light">{errors.confirmPassword.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-12 bg-black hover:bg-gray-900 text-white font-medium rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Updating password...</span>
                    </>
                  ) : (
                    <>
                      Reset password
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            </>
          )}

          <div className="mt-12 text-center">
            <Link to="/login" className="text-sm text-gray-600 hover:text-black font-light transition-colors">
              Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
